import Money from "./Money";

/**
 * A per-share price: the one quantity in the app that needs more precision
 * than Money's integer hundredths (private-company 409A prices carry up to
 * five decimals — $0.60875). Stored at rest as a decimal string exactly as
 * the source document states it; parsed once at the entity→domain boundary
 * into this class, so application code never touches the raw string and
 * misuse (lexicographic comparison, ad-hoc parseFloat) is unreachable
 * rather than merely discouraged.
 *
 * Internally the value is integer micro-dollars (1e-6 units) — an
 * implementation detail nothing outside this file sees. All money
 * arithmetic exits through {@link SharePrice.times}, which returns a Money
 * in integer cents; from there Money's own currency-mismatch throws guard
 * the rest of the pipeline.
 *
 * Parsing is pure string math (split on '.', pad the fraction) — no float
 * anywhere, so any canonical string round-trips exactly.
 */
class SharePrice {
  readonly currency: string;
  private readonly micros: number;

  private constructor(micros: number, currency: string) {
    this.micros = micros;
    this.currency = currency;
  }

  /**
   * Parse a canonical decimal string: optional leading '$' and surrounding
   * whitespace tolerated, then digits with at most six decimals — no signs,
   * commas, or exponents. Throws on anything else. The integer part is
   * capped at nine digits so micros stay far inside safe-integer range.
   */
  static parse = (raw: string, currency: string): SharePrice => {
    const cleaned = raw.trim().replace(/^\$/, "");
    const match = /^(\d{1,9})(?:\.(\d{1,6}))?$/.exec(cleaned);
    if (!match) {
      throw new Error(
        `Invalid share price "${raw}": expected a plain decimal with at most six decimal places`,
      );
    }
    const whole = Number(match[1]);
    const fraction = Number((match[2] ?? "").padEnd(6, "0"));
    return new SharePrice(whole * 1_000_000 + fraction, currency);
  };

  /** Like {@link SharePrice.parse} but returns null instead of throwing. */
  static tryParse = (raw: string, currency: string): SharePrice | null => {
    try {
      return SharePrice.parse(raw, currency);
    } catch {
      return null;
    }
  };

  /**
   * The money exit: this price times a whole number of shares, as Money in
   * integer cents (micros → cents rounds half away from zero; the product
   * is formed in BigInt so magnitude never silently mis-rounds). Shares may
   * be negative — a draw reversal — but must be integers.
   */
  times = (shares: number): Money => {
    if (!Number.isSafeInteger(shares)) {
      throw new Error(`SharePrice.times: shares must be an integer, got ${shares}`);
    }
    const product = BigInt(this.micros) * BigInt(shares);
    const divisor = BigInt(10_000);
    const half = divisor / BigInt(2);
    const negative = product < BigInt(0);
    const magnitude = negative ? -product : product;
    const cents = (magnitude + half) / divisor;
    const signed = Number(negative ? -cents : cents);
    if (!Number.isSafeInteger(signed)) {
      throw new Error("SharePrice.times: result exceeds safe integer range");
    }
    return Money.fromInt(signed, this.currency);
  };

  /**
   * This price times a fractional quantity (brokerage shares, fund units,
   * crypto), as Money in integer cents, rounded half away from zero once.
   * The quantity is taken at fifteen significant digits, exactly, in
   * BigInt: a decimal place count would drop the digits of a tiny crypto
   * quantity.
   */
  timesQuantity = (quantity: number): Money => {
    if (!Number.isFinite(quantity)) {
      throw new Error(`SharePrice.timesQuantity: quantity must be finite, got ${quantity}`);
    }
    // quantity = digits × 10^shift, digits a fifteen-digit integer.
    const [mantissa, exponent] = quantity.toExponential(14).split("e");
    const digits = BigInt(mantissa.replace(".", ""));
    const shift = Number(exponent) - 14;
    const tenTo = (n: number) => BigInt(`1${"0".repeat(n)}`);
    const product = BigInt(this.micros) * digits * (shift > 0 ? tenTo(shift) : BigInt(1));
    const divisor = BigInt(10_000) * (shift < 0 ? tenTo(-shift) : BigInt(1));
    const half = divisor / BigInt(2);
    const negative = product < BigInt(0);
    const magnitude = negative ? -product : product;
    const cents = (magnitude + half) / divisor;
    const signed = Number(negative ? -cents : cents);
    if (!Number.isSafeInteger(signed)) {
      throw new Error("SharePrice.timesQuantity: result exceeds safe integer range");
    }
    return Money.fromInt(signed, this.currency);
  };

  /**
   * Sum of price × shares terms, rounded ONCE at the end: rounding each
   * term to cents independently drifts by up to half a cent per term
   * (an event drawing many lots would accumulate the error). Terms must
   * share a currency; at least one term is required.
   */
  static sumTimes = (
    terms: ReadonlyArray<readonly [SharePrice, number]>,
  ): Money => {
    if (terms.length === 0) {
      throw new Error("SharePrice.sumTimes: at least one term is required");
    }
    const currency = terms[0][0].currency;
    let total = BigInt(0);
    for (const [price, shares] of terms) {
      if (price.currency !== currency) {
        throw new Error("Summing share prices in different currencies");
      }
      if (!Number.isSafeInteger(shares)) {
        throw new Error(
          `SharePrice.sumTimes: shares must be an integer, got ${shares}`,
        );
      }
      total += BigInt(price.micros) * BigInt(shares);
    }
    const divisor = BigInt(10_000);
    const half = divisor / BigInt(2);
    const negative = total < BigInt(0);
    const magnitude = negative ? -total : total;
    const cents = (magnitude + half) / divisor;
    const signed = Number(negative ? -cents : cents);
    if (!Number.isSafeInteger(signed)) {
      throw new Error("SharePrice.sumTimes: result exceeds safe integer range");
    }
    return Money.fromInt(signed, currency);
  };

  /** Price delta (may be negative), e.g. sale price minus basis. */
  minus = (other: SharePrice): SharePrice => {
    if (this.currency !== other.currency) {
      throw new Error("Subtracting share prices in different currencies");
    }
    return new SharePrice(this.micros - other.micros, this.currency);
  };

  /** Numeric ordering: negative, zero, or positive like a comparator. */
  compare = (other: SharePrice): number => {
    if (this.currency !== other.currency) {
      throw new Error("Comparing share prices in different currencies");
    }
    return this.micros - other.micros;
  };

  equals = (other: SharePrice): boolean =>
    this.currency === other.currency && this.micros === other.micros;

  /**
   * Canonical decimal string — what gets stored and sent over GraphQL.
   * Minimum two decimals, trailing zeros beyond that stripped: "63.00",
   * "0.60875", "10.406". Round-trips through parse exactly.
   */
  toString = (): string => {
    const negative = this.micros < 0;
    const magnitude = Math.abs(this.micros);
    const whole = Math.floor(magnitude / 1_000_000);
    const fraction = String(magnitude % 1_000_000).padStart(6, "0");
    const trimmed = fraction.replace(/0+$/, "").padEnd(2, "0");
    return `${negative ? "-" : ""}${whole}.${trimmed}`;
  };

  /** "$0.60875" for USD; "12.50 EUR"-style suffix for anything else. */
  format = (): string =>
    this.currency === "USD"
      ? `$${this.toString()}`
      : `${this.toString()} ${this.currency}`;

  /**
   * The price as a plain number — for chart scales and axes ONLY, never for
   * money math (that goes through {@link SharePrice.times}).
   */
  toNumber = (): number => this.micros / 1_000_000;
}

export default SharePrice;
